<template>
  <section class="isp-user-list">
    <b-card-code title="ISP Connections">
      <template #header-center>
        <div class="stats-cards">
          <div class="stat-card stat-card--primary">
            <div class="stat-icon">
              <feather-icon icon="CreditCardIcon" size="16" />
            </div>
            <div class="stat-info">
              <small>Total Bill</small>
              <div class="stat-value">{{ formatCurrency(totalBill) }}</div>
            </div>
          </div>
          <div class="stat-card stat-card--success">
            <div class="stat-icon">
              <feather-icon icon="UserCheckIcon" size="16" />
            </div>
            <div class="stat-info">
              <small>Active</small>
              <div class="stat-value">{{ totalActive }}</div>
            </div>
          </div>
          <div class="stat-card stat-card--danger">
            <div class="stat-icon">
              <feather-icon icon="UserXIcon" size="16" />
            </div>
            <div class="stat-info">
              <small>Inactive</small>
              <div class="stat-value">{{ totalInactive }}</div>
            </div>
          </div>
          <div class="stat-card stat-card--warning">
            <div class="stat-icon">
              <feather-icon icon="AlertTriangleIcon" size="16" />
            </div>
            <div class="stat-info">
              <small>Inactive Bill</small>
              <div class="stat-value">{{ formatCurrency(inactiveBill) }}</div>
            </div>
          </div>
        </div>
      </template>
      <template #header-actions>
        <div class="d-flex align-items-center flex-wrap justify-content-end header-actions">
          <div class="d-flex align-items-center mb-1">
            <b-button
              size="sm"
              variant="primary"
              :to="{ name: 'isp-user-name-create' }"
            >
              <feather-icon icon="PlusIcon" />
              Add New
            </b-button>
            <b-button
              size="sm"
              class="ml-1"
              variant="primary"
              :to="{ name: 'isp-user-name-create1' }"
            >
              <feather-icon icon="PlusIcon" />
              Add New V2
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="ml-1"
              @click="exportCsv"
            >
              Export CSV
            </b-button>
          </div>
        </div>
      </template>

      <div class="filter-bar premium-panel">
        <div class="filter-grid">
          <b-form-group label="MikroTik Status">
            <b-form-select v-model="selectedMikrotikStatus" :options="mikrotikStatusOptions" />
          </b-form-group>
          <b-form-group label="Status">
            <b-form-select v-model="selectedStatus" :options="statusOptions" />
          </b-form-group>
          <b-form-group label="Router">
            <b-form-select v-model="selectedRouter" :options="routerOptions" />
          </b-form-group>
          <b-form-group label="Package">
            <b-form-select v-model="selectedPackage" :options="packageOptions" />
          </b-form-group>
          <b-form-group label="Search">
            <div class="search-inline">
              <b-form-input
                v-model="searchInput"
                placeholder="Search by name, username, mobile..."
                type="text"
                class="d-inline-block"
              />
              <div class="filter-inline-action">
                <small class="text-muted">Applied: <span class="filter-count">{{ appliedFilters }}</span></small>
                <b-button size="sm" variant="outline-secondary" class="ml-1" @click="resetFilters">
                  <feather-icon icon="Trash2Icon" size="12" class="mr-25" />
                  Clear
                </b-button>
              </div>
            </div>
          </b-form-group>
        </div>
      </div>

      <div v-if="isLoading" class="list-skeleton">
        <div class="skeleton-row" v-for="n in 6" :key="n"></div>
      </div>

      <div v-else-if="isMobileView" ref="mobileVirtualList" class="mobile-cards mobile-virtual-list">
        <div
          v-if="mobileTopSpacerHeight > 0"
          class="mobile-virtual-spacer"
          :style="{ height: `${mobileTopSpacerHeight}px` }"
        ></div>
        <b-card
          v-for="row in mobileVirtualRows"
          :key="row.id"
          ref="mobileCard"
          ref-in-for
          class="user-card mb-1"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h6 class="mb-0">
                <b-link
                  v-if="row.id"
                  :href="userEditHref(row)"
                  target="_blank"
                  rel="noopener"
                  class="mobile-user-link"
                >
                  {{ row.user_name }}
                </b-link>
                <span v-else class="text-primary">
                  {{ row.user_name }}
                </span>
              </h6>
              <small class="text-muted">{{ row.name }}</small>
            </div>
            <b-badge :variant="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? 'Active' : 'Inactive' }}
            </b-badge>
          </div>
          <div class="card-meta mt-1">
            <div class="meta-item">
              <small class="text-muted">Mobile</small>
              <div>{{ row.mobile }}</div>
            </div>
            <div class="meta-item">
              <small class="text-muted">Package</small>
              <div>{{ row.package_name }}</div>
            </div>
            <div class="meta-item">
              <small class="text-muted">Live Status</small>
              <b-badge :variant="liveStatusVariant(row.id)" class="text-capitalize">
                {{ liveStatusLabel(row.id) }}
              </b-badge>
            </div>
          </div>
          <div class="card-actions mt-1">
            <b-button size="sm" variant="outline-primary" :to="{ name: 'isp-user-name-edit-v2', params: { id: row.id } }">
              Edit
            </b-button>
            <b-button size="sm" variant="outline-secondary" :to="{ name: 'isp-user-get-live-data', params: { usernameId: row.id } }">
              Live
            </b-button>
          </div>
        </b-card>
        <div
          v-if="mobileBottomSpacerHeight > 0"
          class="mobile-virtual-spacer"
          :style="{ height: `${mobileBottomSpacerHeight}px` }"
        ></div>
      </div>

      <vue-good-table
        v-else
        :columns="columns"
        :rows="displayRows"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
        :sort-options="{
          enabled: true,
          // initialSortBy: {field: 'last_app_hit', type: 'asc'}
        }"
      >
      <template slot="table-header" slot-scope="props">
        <span v-if="props.column.field === 'live_status'" class="icon-header">
          <span :id="`hdr-${props.column.field}`">
            <feather-icon icon="WifiIcon" size="14" />
            <small class="text-muted ml-25">Conn</small>
          </span>
          <b-tooltip :target="`hdr-${props.column.field}`" title="Connection Status" />
        </span>
        <span v-else-if="props.column.field === 'status'" class="icon-header">
          <span :id="`hdr-${props.column.field}`">
            <feather-icon icon="UserCheckIcon" size="14" />
            <small class="text-muted ml-25">Acct</small>
          </span>
          <b-tooltip :target="`hdr-${props.column.field}`" title="Account Status" />
        </span>
        
        <span v-else>
          {{ props.column.label }}
        </span>
      </template>
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div
          v-if="props.column.field === 'user_name'"
          class="text-nowrap"
        >
          <b-link
            :to="{
              name: 'isp-user-name-edit',
              params: { usernameId: props.row.id },
            }"
            class="font-weight-bold text-primary"
            :title="props.row.user_name"
          >
            <span class="truncate">{{ props.row.user_name }}</span>

          </b-link>
          <b-button
            :id="`copy-username-${props.row.id}`"
            size="sm"
            variant="link"
            class="p-0 ml-50 align-baseline"
            @click="copyToClipboard(props.row.user_name)"
          >
            <feather-icon icon="CopyIcon" size="14" />
          </b-button>
          <b-tooltip :target="`copy-username-${props.row.id}`" title="Copy username" />
        </div>
        <span v-else-if="props.column.field === 'name'" class="text-nowrap">
          <span class="truncate" :title="props.row.name">{{ props.row.name }}</span>
        </span>
        <span v-else-if="props.column.field === 'live_status'">
          <b-link
            :to="{
              name: 'isp-user-get-live-data',
              params: { usernameId: props.row.id },
            }"
            :id="`conn-${props.row.id}`"
            class="wifi-status icon-link"
          >
            <svg
              v-if="liveStatusLabel(props.row.id) === 'online'"
              class="wifi-wave"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path class="wifi-arc arc-1" d="M8.5 12.5c2-2 5-2 7 0" />
              <path class="wifi-arc arc-2" d="M6 10c3.3-3.3 8.7-3.3 12 0" />
              <path class="wifi-arc arc-3" d="M3.5 7.5c4.7-4.7 12.3-4.7 17 0" />
              <circle class="wifi-dot" cx="12" cy="16.5" r="1.6" />
            </svg>
            <feather-icon
              v-else
              :icon="liveStatusLabel(props.row.id) === 'offline' ? 'WifiOffIcon' : 'AlertCircleIcon'"
              size="16"
              :class="[
                liveStatusLabel(props.row.id) === 'offline' ? 'text-danger' : 'text-secondary',
                liveStatusLabel(props.row.id) === 'unknown' || liveStatusLabel(props.row.id) === 'checking' ? 'live-status-pulse' : ''
              ]"
            />
          </b-link>
          <b-tooltip
            :target="`conn-${props.row.id}`"
            :title="liveStatusLabel(props.row.id) === 'online' ? 'Click to view live details' : (liveStatusLabel(props.row.id) === 'offline' ? 'User is offline' : 'Router unreachable')"
          />
        </span>
        <span v-else-if="props.column.field === 'status'">
          <span :id="`acct-${props.row.id}`">
            <feather-icon
              :icon="props.row.status === 1 ? 'UserCheckIcon' : 'UserXIcon'"
              size="16"
              :class="props.row.status === 1 ? 'text-success' : 'text-danger'"
            />
          </span>
          <b-tooltip :target="`acct-${props.row.id}`" :title="props.row.status === 1 ? 'Active' : 'Inactive'" />
        </span>
        <span v-else-if="props.column.field === 'package_name'" class="text-nowrap">
          <span class="truncate" :title="props.row.package_name">{{ props.row.package_name }}</span>
        </span>
        <span v-else-if="props.column.field === 'billing_price'">
          <b-badge :variant="props.row.status === 1 ? 'light-success' : 'light-danger'">
            {{ formatCurrency(props.row.billing_price) }}
          </b-badge>
        </span>
        <span v-else-if="props.column.field === 'mobile'">
          <span class="mono-compact">{{ `+${props.row.mobile}` }}</span>
        </span>
        <span v-else-if="props.column.field === 'router_name'" class="text-nowrap">
          {{ props.row.router_name }}
        </span>
        <span v-else-if="props.column.field === 'last_app_hit'">
          {{ formatDate(props.row.last_app_hit) }}
        </span>
        <span v-else-if="props.column.field === 'pair_code'">
          <code class="mono-compact">{{ props.row.pair_code }}</code>
        </span>

        <div
          v-else-if="props.column.field === 'action'"
          class="text-nowrap"
        >
          <b-link
            :to="{
              name: 'isp-user-name-edit-v2',
              params: { id: props.row.id },
            }"
            class="font-weight-bold"
          >
            <b-button size="sm" variant="outline-primary" class="px-1 py-0">
              <feather-icon icon="Edit2Icon" size="14" />
            </b-button>

          </b-link>
        </div>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap"> Showing 1 to </span>
            <b-form-select
              v-model="pageLength"
              :options="['100', '300', '500','1000']"
              class="mx-1"
              @input="
                (value) => props.perPageChanged({ currentPerPage: value })
              "
            />
            <span class="text-nowrap"> of {{ props.total }} entries </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value) => props.pageChanged({ currentPage: value })"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card-code>
  </section>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BBadge,
  BCard,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BLink, BButton, BSpinner, BTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
// eslint-disable-next-line import/extensions
import { USERNAME_LIST, USERNAME_LIVE_STATUS } from '@core/services/api.js'

export default {
  components: {
    BLink,
    BCardCode,
    BCard,
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BTooltip,
  },
  data() {
    return {
      pageLength: 100,
      isLoading: false,
      error: null,
      dir: false,
      isp_id: null,
      searchInput: '',
      debounceTimer: null,
      selectedStatus: 'All',
      selectedRouter: 'All',
      selectedPackage: 'All',
      selectedMikrotikStatus: 'All',
      statusOptions: ['All', 'Active', 'Inactive'],
      mikrotikStatusOptions: ['All', 'Online', 'Offline', 'Unknown'],
      liveStatusMap: {},
      liveStatusCheckedAt: null,
      liveStatusLoading: false,
      statusFetchTimer: null,
      isMobileView: false,
      mobileItemHeight: 196,
      mobileOverscan: 4,
      mobileVirtualStart: 0,
      mobileVirtualEnd: 12,
      mobileVirtualTicking: false,
      columns: [
        {
          label: '',
          field: 'live_status',
          thClass: 'text-center font-weight-bold text-nowrap col-live-status col-fit',
          tdClass: 'text-center col-live-status col-fit',
        },
        {
          label: '',
          field: 'status',
          thClass: 'text-center font-weight-bold text-nowrap col-status col-fit',
          tdClass: 'text-center col-status col-fit',
        },
        {
          label: 'PPP Username',
          field: 'user_name',
          thClass: 'text-center font-weight-bold text-nowrap col-username',
          tdClass: 'text-nowrap text-primary font-weight-bold col-username',
        },
        {
          label: 'Customer',
          field: 'name',
          thClass: 'text-center font-weight-bold text-nowrap col-name',
          tdClass: 'text-nowrap text-dark col-name',
        },
        {
          label: 'Package',
          field: 'package_name',
          thClass: 'text-center font-weight-bold text-nowrap col-package',
          tdClass: 'text-nowrap text-dark col-package',
        },
        {
          label: 'Monthly Bill',
          field: 'billing_price',
          thClass: 'text-center font-weight-bold text-nowrap col-amount col-fit',
          tdClass: 'text-center text-dark font-weight-bold col-amount col-fit',
        },
        {
          label: 'Mobile',
          field: 'mobile',
          thClass: 'text-center font-weight-bold text-nowrap col-mobile col-fit',
          tdClass: 'text-center col-mobile col-fit',
        },
        {
          label: 'Router',
          field: 'router_name',
          thClass: 'text-center font-weight-bold text-nowrap col-router',
          tdClass: 'text-nowrap text-dark col-router',
        },
        {
          label: 'Last Login',
          field: 'last_app_hit',
          thClass: 'text-center font-weight-bold text-nowrap col-last-login',
          tdClass: 'text-nowrap text-secondary col-last-login',
        },
        {
          label: 'Pair Code',
          field: 'pair_code',
          thClass: 'text-center font-weight-bold text-nowrap col-pair col-fit',
          tdClass: 'text-center col-pair col-fit',
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center font-weight-bold text-nowrap col-action col-fit',
          tdClass: 'text-right col-action col-fit',
        },
      ],
      rows: [],
      searchTerm: '',
      total_sale_value: null,
      total_active_ppp: null,
      total_inactive_ppp: null,
    }
  },
  computed: {
    appliedFilters() {
      let count = 0
      if (this.selectedStatus && this.selectedStatus !== 'All') count += 1
      if (this.selectedMikrotikStatus && this.selectedMikrotikStatus !== 'All') count += 1
      if (this.selectedRouter && this.selectedRouter !== 'All') count += 1
      if (this.selectedPackage && this.selectedPackage !== 'All') count += 1
      if (this.searchInput && this.searchInput.trim().length > 0) count += 1
      return count
    },
    displayRows() {
      // Apply filters to base rows
      const statusFilter = this.selectedStatus
      const routerFilter = this.selectedRouter
      const packageFilter = this.selectedPackage
      const mikrotikFilter = this.selectedMikrotikStatus
      const term = (this.searchTerm || '').trim().toLowerCase()
      return this.rows.filter(r => {
        const statusOk = statusFilter === 'All' || (statusFilter === 'Active' ? r.status === 1 : r.status !== 1)
        const routerOk = routerFilter === 'All' || r.router_name === routerFilter
        const packageOk = packageFilter === 'All' || r.package_name === packageFilter
        const mtStatus = this.liveStatusLabel(r.id)
        const mikrotikOk = mikrotikFilter === 'All'
          || (mikrotikFilter === 'Online' && mtStatus === 'online')
          || (mikrotikFilter === 'Offline' && mtStatus === 'offline')
          || (mikrotikFilter === 'Unknown' && (mtStatus === 'unknown' || mtStatus === 'checking'))
        const searchOk = !term || (r._search || '').includes(term)
        return statusOk && routerOk && packageOk && mikrotikOk && searchOk
      })
    },
    mobileVirtualRows() {
      const total = this.displayRows.length
      if (!total) return []
      const start = Math.max(0, Math.min(this.mobileVirtualStart, total))
      const end = Math.max(start, Math.min(this.mobileVirtualEnd, total))
      return this.displayRows.slice(start, end)
    },
    mobileTopSpacerHeight() {
      if (!this.isMobileView) return 0
      return this.mobileVirtualStart * this.mobileItemHeight
    },
    mobileBottomSpacerHeight() {
      if (!this.isMobileView) return 0
      const remaining = this.displayRows.length - this.mobileVirtualEnd
      return remaining > 0 ? remaining * this.mobileItemHeight : 0
    },
    routerOptions() {
      const set = new Set(['All'])
      this.rows.forEach(r => r.router_name && set.add(r.router_name))
      return Array.from(set)
    },
    packageOptions() {
      const set = new Set(['All'])
      this.rows.forEach(r => r.package_name && set.add(r.package_name))
      return Array.from(set)
    },
    totalBill() {
      if (this.rows.length) {
        // eslint-disable-next-line camelcase
        return this.rows.reduce((n, { billing_price }) => n + billing_price, 0)
      }
      return 0
    },
    totalActive() {
      if (this.rows.length) {
        // eslint-disable-next-line camelcase
        return this.rows.filter(item => item.status === 1).length
      }
      return 0
    },
    totalInactive() {
      if (this.rows.length) {
        // eslint-disable-next-line camelcase
        return this.rows.filter(item => item.status !== 1).length
      }
      return 0
    },
    inactiveBill() {
      if (this.rows.length) {
        return this.rows
          .filter(item => item.status !== 1)
          .reduce((sum, item) => sum + Number(item.billing_price || 0), 0)
      }
      return 0
    },
  },
  created() {
  },
  mounted() {
    this.userNameList(this.isp_id)
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
    this.$nextTick(() => {
      this.updateMobileVirtualWindow()
      this.refreshMobileItemHeight()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleWindowScroll)
  },
  methods: {
    userEditHref(row) {
      if (!row || !row.id) return '#'
      return this.$router.resolve({
        name: 'isp-user-name-edit',
        params: { usernameId: row.id },
      }).href
    },
    copyToClipboard(text) {
      try {
        const el = document.createElement('textarea')
        el.value = String(text || '')
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        if (this.$bvToast) this.$bvToast.toast('Username copied', { variant: 'success', solid: true, autoHideDelay: 1000 })
      } catch (e) {
        // ignore
      }
    },
    formatCurrency(amount) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'BDT', currencyDisplay: 'code', minimumFractionDigits: 0 }).format(amount).replace('BDT', '').trim()
      } catch (e) {
        return amount
      }
    },
    formatDate(value) {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d)
    },
    exportCsv() {
      const headers = this.columns.map(c => c.label)
      const fields = this.columns.map(c => c.field)
      const csvRows = [headers.join(',')]
      this.displayRows.forEach(r => {
        const line = fields.map(f => JSON.stringify(r[f] !== undefined ? r[f] : '')).join(',')
        csvRows.push(line)
      })
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ppp-users.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    userNameList(ispId) {
      this.isLoading = true
      this.error = null
      this.$http
        .get(`${process.env.VUE_APP_BASEURL + USERNAME_LIST}?isp_id=${ispId}`)
        .then(res => {
          const rawRows = res.data.data.userList || []
          this.rows = rawRows.map(r => ({
            ...r,
            _search: [
              r.user_name,
              r.name,
              r.mobile,
              r.package_name,
              r.router_name,
              r.pair_code,
            ].map(v => String(v || '').toLowerCase()).join(' '),
          }))
          this.syncVirtualWindowAndStatus()
          // Initialize filters if not set
          if (!this.selectedStatus) this.selectedStatus = 'All'
        })
        .catch(() => {
          this.error = 'Failed to load data.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    resetFilters() {
      this.selectedStatus = 'All'
      this.selectedMikrotikStatus = 'All'
      this.selectedRouter = 'All'
      this.selectedPackage = 'All'
      this.searchInput = ''
      this.searchTerm = ''
      this.mobileVirtualStart = 0
      this.mobileVirtualEnd = 12
      this.scheduleVirtualWindowUpdate()
      this.scheduleLiveStatusFetch()
    },
    handleResize() {
      this.isMobileView = window.innerWidth < 992
      this.scheduleVirtualWindowUpdate()
    },
    handleWindowScroll() {
      if (!this.isMobileView) return
      this.scheduleVirtualWindowUpdate()
    },
    scheduleVirtualWindowUpdate() {
      if (this.mobileVirtualTicking) return
      this.mobileVirtualTicking = true
      window.requestAnimationFrame(() => {
        this.mobileVirtualTicking = false
        this.updateMobileVirtualWindow()
      })
    },
    updateMobileVirtualWindow() {
      if (!this.isMobileView) {
        this.mobileVirtualStart = 0
        this.mobileVirtualEnd = this.displayRows.length
        return
      }

      const total = this.displayRows.length
      if (!total) {
        this.mobileVirtualStart = 0
        this.mobileVirtualEnd = 0
        return
      }

      const listEl = this.$refs.mobileVirtualList
      if (!listEl) {
        this.mobileVirtualStart = 0
        this.mobileVirtualEnd = Math.min(total, 12)
        return
      }

      const rect = listEl.getBoundingClientRect()
      const listTop = rect.top + window.scrollY
      const viewportTop = window.scrollY
      const viewportBottom = viewportTop + window.innerHeight
      const localTop = Math.max(0, viewportTop - listTop)
      const localBottom = Math.max(0, viewportBottom - listTop)

      const start = Math.max(0, Math.floor(localTop / this.mobileItemHeight) - this.mobileOverscan)
      const end = Math.min(total, Math.ceil(localBottom / this.mobileItemHeight) + this.mobileOverscan)

      this.mobileVirtualStart = start
      this.mobileVirtualEnd = Math.max(start + 1, end)
    },
    refreshMobileItemHeight() {
      if (!this.isMobileView) return
      const refs = this.$refs.mobileCard
      const firstCard = Array.isArray(refs) ? refs[0] : refs
      if (!firstCard || !firstCard.$el) return
      const measured = firstCard.$el.offsetHeight
      if (measured && Math.abs(measured - this.mobileItemHeight) > 4) {
        this.mobileItemHeight = measured
        this.scheduleVirtualWindowUpdate()
      }
    },
    syncVirtualWindowAndStatus() {
      this.mobileVirtualStart = 0
      this.mobileVirtualEnd = 12
      this.$nextTick(() => {
        this.updateMobileVirtualWindow()
        this.refreshMobileItemHeight()
      })
      this.scheduleLiveStatusFetch()
    },
    scheduleLiveStatusFetch() {
      clearTimeout(this.statusFetchTimer)
      this.statusFetchTimer = setTimeout(() => {
        this.fetchLiveStatus()
      }, 400)
    },
    async fetchLiveStatus() {
      const sourceRows = this.isMobileView ? this.mobileVirtualRows : this.displayRows
      const ids = sourceRows.map(r => r.id).filter(Boolean)
      if (!ids.length) return

      // Only fetch statuses that are not already cached.
      const idsToFetch = ids.filter(id => !this.liveStatusMap[id])
      if (!idsToFetch.length) return

      this.liveStatusLoading = true
      const chunks = this.chunkArray(idsToFetch, 200)
      const statusMap = { ...this.liveStatusMap }
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const chunk of chunks) {
          // eslint-disable-next-line no-await-in-loop
          const res = await this.$http.post(process.env.VUE_APP_BASEURL + USERNAME_LIVE_STATUS, {
            user_ids: chunk,
          })
          const data = (res.data && res.data.data) ? res.data.data : {}
          const statuses = data.statuses || {}
          Object.keys(statuses).forEach(id => {
            statusMap[id] = statuses[id]
          })
          this.liveStatusCheckedAt = data.checked_at || this.liveStatusCheckedAt
        }
      } catch (e) {
        ids.forEach(id => {
          if (!statusMap[id]) statusMap[id] = 'unknown'
        })
      } finally {
        this.liveStatusMap = statusMap
        this.liveStatusLoading = false
      }
    },
    liveStatusLabel(id) {
      const status = this.liveStatusMap[id]
      if (this.liveStatusLoading && !status) return 'checking'
      if (status === 'online') return 'online'
      if (status === 'offline') return 'offline'
      return 'unknown'
    },
    liveStatusVariant(id) {
      const status = this.liveStatusMap[id]
      if (this.liveStatusLoading && !status) return 'light-secondary'
      if (status === 'online') return 'success'
      if (status === 'offline') return 'danger'
      return 'secondary'
    },
    chunkArray(arr, size) {
      const chunks = []
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size))
      }
      return chunks
    },
  },
  watch: {
    searchInput(val) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.searchTerm = val
        this.syncVirtualWindowAndStatus()
      }, 80)
    },
    selectedStatus() {
      this.syncVirtualWindowAndStatus()
    },
    selectedMikrotikStatus() {
      this.syncVirtualWindowAndStatus()
    },
    selectedRouter() {
      this.syncVirtualWindowAndStatus()
    },
    selectedPackage() {
      this.syncVirtualWindowAndStatus()
    },
    displayRows() {
      this.$nextTick(() => {
        this.updateMobileVirtualWindow()
        this.refreshMobileItemHeight()
      })
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-good-table.scss";

/* Pro-level table typography and spacing */
::v-deep .vgt-table {
  font-size: 0.82rem;
  table-layout: auto;
}
::v-deep .vgt-table th, ::v-deep .vgt-table td {
  padding: 0.2rem 0.4rem;
}
::v-deep .vgt-table thead th {
  font-weight: 600;
  text-align: center;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 2;
  white-space: nowrap;
}
::v-deep .vgt-table td {
  color: #212529;
  vertical-align: middle;
}
::v-deep .vgt-table tbody tr:hover td {
  background-color: #f6f9ff;
}

/* Header metrics badges */
::v-deep .stats-badge {
  font-size: 1rem;
  padding: 0.4rem 0.6rem;
}

/* Column widths */
::v-deep th.col-name, ::v-deep td.col-name { min-width: 110px; }
::v-deep th.col-username, ::v-deep td.col-username { min-width: 110px; }
::v-deep th.col-mobile, ::v-deep td.col-mobile { width: 68px; }
::v-deep th.col-live, ::v-deep td.col-live { width: 42px; }
::v-deep th.col-live-status, ::v-deep td.col-live-status { width: 42px; }
::v-deep th.col-status, ::v-deep td.col-status { width: 52px; }
::v-deep th.col-package, ::v-deep td.col-package { min-width: 140px; }
::v-deep th.col-amount, ::v-deep td.col-amount { width: 110px; }
::v-deep th.col-pair, ::v-deep td.col-pair { width: 68px; }
::v-deep th.col-router, ::v-deep td.col-router { min-width: 140px; }
::v-deep th.col-last-login, ::v-deep td.col-last-login { min-width: 150px; }
::v-deep th.col-status, ::v-deep td.col-status { width: 96px; }
::v-deep th.col-action, ::v-deep td.col-action { width: 96px; }

/* Fit-to-content helper */
::v-deep .col-fit { width: 1%; }

/* Truncate helper for tight columns */
::v-deep .truncate {
  max-width: 90px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/* Sticky right actions column */
::v-deep th.col-action, ::v-deep td.col-action {
  position: sticky;
  right: 0;
  background: #fff;
  z-index: 3;
  box-shadow: -1px 0 0 rgba(0,0,0,0.05);
}

/* Compact monospace/link helpers for tight columns */
::v-deep .mono-compact {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.72rem;
}
::v-deep .link-reset {
  color: inherit;
  text-decoration: none;
}

.isp-user-list {
  background: transparent;
}

.filter-bar {
  padding: 0.9rem 1rem;
  border: 1px solid #edf0f5;
  border-radius: 0.9rem;
  background: #fff;
  margin-bottom: 1rem;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.05);
}

.stats-pills {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.stats-badge {
  font-size: 0.78rem;
  letter-spacing: 0.01em;
}

.stats-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.6rem;
  border-radius: 0.9rem;
  background: linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.9) 100%);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.18);
  min-width: 130px;
  position: relative;
  overflow: hidden;
}

.stat-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(99,102,241,0.1), transparent 60%);
  opacity: 0.5;
  pointer-events: none;
}

.stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.stat-info small {
  display: block;
  font-size: 0.6rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
}

.stat-card--primary .stat-icon { background: #6366f1; }
.stat-card--success .stat-icon { background: #22c55e; }
.stat-card--danger .stat-icon { background: #ef4444; }
.stat-card--warning .stat-icon { background: #f59e0b; }

.header-actions .btn {
  border-radius: 0.85rem;
  padding: 0.7rem 1.3rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.header-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.16);
}

.header-actions .btn-primary {
  background: linear-gradient(135deg, #5b4bff 0%, #7a8bff 100%);
  border: 0;
}

.header-actions .btn-outline-secondary {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

@media (max-width: 1200px) {
  .header-actions .btn {
    padding: 0.5rem 0.9rem;
    font-size: 0.82rem;
  }
}

@media (max-width: 992px) {
  .header-actions .btn {
    padding: 0.45rem 0.75rem;
    font-size: 0.78rem;
  }
  .header-actions .d-flex {
    gap: 0.4rem;
  }
}

.filter-count {
  display: inline-block;
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 600;
  text-align: center;
}

.premium-panel label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.premium-panel .form-control {
  height: 40px;
  border-radius: 0.6rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(120px, 0.7fr) minmax(140px, 0.8fr) minmax(120px, 0.7fr) minmax(140px, 0.9fr) minmax(260px, 1.8fr);
  gap: 0.75rem;
  align-items: end;
}

.filter-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.35rem;
}

.filter-inline-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.filter-inline-action .btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1;
}

.search-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-inline .form-control {
  min-width: 0;
}

@media (max-width: 1199.98px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-grid .form-group:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 991.98px) {
  .filter-bar {
    padding: 0.75rem;
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }

  .filter-grid .form-group:last-child {
    grid-column: 1 / -1;
  }

  .search-inline {
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .search-inline .form-control {
    flex: 1 1 100%;
    width: 100%;
  }

  .filter-inline-action {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 575.98px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}

.list-skeleton .skeleton-row {
  height: 42px;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: skeleton-move 1.2s ease-in-out infinite;
  margin-bottom: 0.5rem;
  position: relative;
  overflow: hidden;
}

.list-skeleton .skeleton-row::after {
  content: '';
  position: absolute;
  top: 0;
  left: -30%;
  width: 30%;
  height: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%);
  animation: skeleton-sweep 1.3s linear infinite;
}

.mobile-cards .user-card {
  border: 1px solid #eef1f6;
  border-radius: 0.75rem;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  min-height: 188px;
}

.mobile-virtual-list {
  position: relative;
}

.mobile-virtual-spacer {
  width: 100%;
}

.mobile-user-link {
  color: #5b4bff;
  font-weight: 700;
  text-decoration: none;
}

.mobile-user-link:hover,
.mobile-user-link:focus {
  color: #4338ca;
  text-decoration: underline;
}

.card-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.icon-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.icon-link {
  color: inherit;
  text-decoration: none;
  border-bottom: 0 !important;
}

.icon-link::before,
.icon-link::after {
  content: none !important;
}


@keyframes skeleton-move {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes skeleton-sweep {
  0% { transform: translateX(0); }
  100% { transform: translateX(430%); }
}

.live-status-pulse {
  animation: live-status-pulse 1s ease-in-out infinite;
}

@keyframes live-status-pulse {
  0% { transform: scale(0.92); opacity: 0.6; }
  60% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.92); opacity: 0.6; }
}

.wifi-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.04);
}

.status-dot--active {
  background: #22c55e;
  box-shadow: 0 0 6px rgba(34,197,94,0.6);
}

.status-dot--inactive {
  background: #ef4444;
  box-shadow: 0 0 6px rgba(239,68,68,0.5);
}

.wifi-wave {
  width: 22px;
  height: 22px;
  display: inline-block;
  color: #16a34a;
  filter: drop-shadow(0 0 6px rgba(22,163,74,0.6));
}

.wifi-wave .wifi-arc {
  fill: none;
  stroke: currentColor;
  stroke-width: 2.6;
  stroke-linecap: round;
  opacity: 0.25;
  animation: wifi-arc 1.3s ease-in-out infinite;
}

.wifi-wave .wifi-arc.arc-1 { animation-delay: 0s; }
.wifi-wave .wifi-arc.arc-2 { animation-delay: 0.2s; }
.wifi-wave .wifi-arc.arc-3 { animation-delay: 0.4s; }

.wifi-wave .wifi-dot {
  fill: currentColor;
  animation: wifi-dot 1.3s ease-in-out infinite;
}

@keyframes wifi-arc {
  0% { opacity: 0.25; }
  40% { opacity: 1; }
  100% { opacity: 0.25; }
}

@keyframes wifi-dot {
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
}
</style>
